import { FormEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import {
  getCenter,
  getGroupRules,
  updateGroupRules,
} from "../../redux/actions/fluxoNSimilar";
import {
  GroupRuleResponse,
  UpdateGroupRuleRequest,
} from "../../types/gruposResponsabilidade";
import { OptionsCenter } from "../../utils/optionsAutocomplete";
import { CustomCheckbox, CustomCheckIcon } from "../customCheckbox/customCheckbox";
import { ButtonGeneric } from "../ButtonGeneric/buttonGeneric";
import InputCheckBox from "../inputCheckBox/inputCheckBox";
import Input from "../fluxo-engenheiro/formBasic/Input";
import ContainerError from "../error/containerError";
import { useNotification } from "../../hooks/useNotification";
import Textnotifications from "../../utils/notifications";
import Notification from "../fluxo-tax/Notification/notification";
import IconAlert from "../../assets/svgs/icon-alert.svg";
import InfoIcon from "../../assets/svgs/alert-info.svg";
import SucessIcon from "../../assets/svgs/sucess.svg";
import PlusButtonFilter from "../../assets/svgs/plusButtonFilter.svg";
import { SkeletonWrapper } from "../SkeletonWrapper/sleketonWrapper";
import "./regrasGrupoForm.css";

type TextRuleFieldId =
  | "materialType"
  | "industrySector"
  | "valuationClass"
  | "hierarchy"
  | "profitCenter";

type RuleFieldId = "center" | TextRuleFieldId;

type RuleValues = Record<RuleFieldId, string[]>;
type EnabledFields = Record<RuleFieldId, boolean>;
type TextInputs = Record<TextRuleFieldId, string>;
type FieldErrors = Partial<Record<RuleFieldId, string>>;

interface CenterRuleFieldConfig {
  id: "center";
  label: string;
  description: string;
  required: boolean;
  multiValue: boolean;
  mode: "center";
}

interface TextRuleFieldConfig {
  id: TextRuleFieldId;
  label: string;
  description: string;
  required: boolean;
  multiValue: boolean;
  mode: "text";
}

type RuleFieldConfig = CenterRuleFieldConfig | TextRuleFieldConfig;

type FieldDisplayConfig = Omit<RuleFieldConfig, "required" | "multiValue">;

interface RegrasGrupoFormProps {
  backToGroupsList?: boolean;
}

const ALL_CENTERS_VALUE = "ALL";

const FIELD_DISPLAY_CONFIG: Record<RuleFieldId, FieldDisplayConfig> = {
  center: {
    id: "center",
    label: "Centro",
    description: "Campo obrigatório",
    mode: "center",
  },
  materialType: {
    id: "materialType",
    label: "Tipo de Material",
    description: "Campo obrigatório",
    mode: "text",
  },
  industrySector: {
    id: "industrySector",
    label: "Setor de Atividade",
    description: "Opcional",
    mode: "text",
  },
  valuationClass: {
    id: "valuationClass",
    label: "Classe de Avaliação",
    description: "Opcional",
    mode: "text",
  },
  hierarchy: {
    id: "hierarchy",
    label: "Hierarquia",
    description: "Opcional",
    mode: "text",
  },
  profitCenter: {
    id: "profitCenter",
    label: "Centro de Lucro",
    description: "Opcional",
    mode: "text",
  },
};

const RULE_FIELDS: RuleFieldConfig[] = [
  {
    id: "center",
    label: "Centro",
    description: "Campo obrigatório",
    required: true,
    multiValue: true,
    mode: "center",
  },
  {
    id: "materialType",
    label: "Tipo de Material",
    description: "Campo obrigatório",
    required: true,
    multiValue: true,
    mode: "text",
  },
  {
    id: "industrySector",
    label: "Setor de Atividade",
    description: "Opcional",
    required: false,
    multiValue: true,
    mode: "text",
  },
  {
    id: "valuationClass",
    label: "Classe de Avaliação",
    description: "Opcional",
    required: false,
    multiValue: true,
    mode: "text",
  },
  {
    id: "hierarchy",
    label: "Hierarquia",
    description: "Opcional",
    required: false,
    multiValue: true,
    mode: "text",
  },
  {
    id: "profitCenter",
    label: "Centro de Lucro",
    description: "Opcional",
    required: false,
    multiValue: true,
    mode: "text",
  },
];

const createInitialValues = (): RuleValues => ({
  center: [],
  materialType: [],
  industrySector: [],
  valuationClass: [],
  hierarchy: [],
  profitCenter: [],
});

const createInitialEnabledFields = (): EnabledFields => ({
  center: true,
  materialType: true,
  industrySector: false,
  valuationClass: false,
  hierarchy: false,
  profitCenter: false,
});

const createInitialTextInputs = (): TextInputs => ({
  materialType: "",
  industrySector: "",
  valuationClass: "",
  hierarchy: "",
  profitCenter: "",
});

const normalizeValue = (value: string) => value.trim().toLocaleLowerCase();

const isRuleFieldId = (field: string): field is RuleFieldId =>
  field in FIELD_DISPLAY_CONFIG;

const createRuleFieldConfig = (rule: GroupRuleResponse): RuleFieldConfig => ({
  ...FIELD_DISPLAY_CONFIG[rule.field as RuleFieldId],
  required: rule.required,
  multiValue: rule.multiValue ?? true,
} as RuleFieldConfig);

const RegrasGrupoForm = ({ backToGroupsList = false }: RegrasGrupoFormProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId?: string }>();
  const groupRulesState = useSelector(
    (state: RootState) => state.sessionGroupRules,
  );
  const [notificationIcon, setNotificationIcon] = useState(SucessIcon);
  const {
    notification,
    labelNotification,
    backgroundColor,
    setNotification,
    showNotification,
  } = useNotification();
  const groupRules = groupId
    ? groupRulesState.rulesByGroupId[groupId]
    : undefined;
  const rulesStatus = groupId
    ? groupRulesState.statusByGroupId[groupId] ?? "idle"
    : "idle";
  const savingStatus = groupId
    ? groupRulesState.savingStatusByGroupId[groupId] ?? "idle"
    : "idle";
  const ruleFields = groupRules?.rules
    ?.filter((rule) => isRuleFieldId(rule.field))
    .map(createRuleFieldConfig) ?? RULE_FIELDS;
  const [values, setValues] = useState<RuleValues>(createInitialValues);
  const [enabledFields, setEnabledFields] = useState<EnabledFields>(
    createInitialEnabledFields,
  );
  const [textInputs, setTextInputs] = useState<TextInputs>(
    createInitialTextInputs,
  );
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [centerInput, setCenterInput] = useState("");
  const [centerOptions, setCenterOptions] = useState<OptionsCenter[]>([]);
  const [pendingCenters, setPendingCenters] = useState<OptionsCenter[]>([]);
  const [allCenters, setAllCenters] = useState(false);
  const [previousCenterValues, setPreviousCenterValues] = useState<string[]>([]);
  const [centerSearchError, setCenterSearchError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const isBusy = isSubmitting || savingStatus === "saving";

  useEffect(() => {
    if (!groupId) return;

    void dispatch(getGroupRules(groupId));
  }, [dispatch, groupId]);

  useEffect(() => {
    if (!groupRules) return;

    const nextValues = createInitialValues();
    const nextEnabledFields = createInitialEnabledFields();
    let nextAllCenters = false;

    groupRules.rules.forEach((rule) => {
      if (!isRuleFieldId(rule.field)) return;

      nextEnabledFields[rule.field] = rule.required || rule.active;

      if (rule.field === "center" && rule.values.includes(ALL_CENTERS_VALUE)) {
        nextAllCenters = true;
        nextValues.center = [];
        return;
      }

      nextValues[rule.field] = [...rule.values];
    });

    setValues(nextValues);
    setEnabledFields(nextEnabledFields);
    setTextInputs(createInitialTextInputs());
    setPendingCenters([]);
    setCenterInput("");
    setCenterOptions([]);
    setAllCenters(nextAllCenters);
    setPreviousCenterValues(nextAllCenters ? [] : nextValues.center);
    setFieldErrors({});
    setCenterSearchError("");
  }, [groupRules]);

  const clearFieldError = (fieldId: RuleFieldId) => {
    setFieldErrors((current) => {
      if (!current[fieldId]) return current;

      const next = { ...current };
      delete next[fieldId];
      return next;
    });
  };

  const clearSavedState = () => {
    setNotification(false);
  };

  const handleCenterSearch = async (value: string) => {
    const search = value.trim();

    setCenterSearchError("");

    if (!search) {
      setCenterOptions([]);
      return;
    }

    try {
      const raw = await dispatch(getCenter({ id: search })).unwrap();
      const mappedOptions = Array.isArray(raw)
        ? raw.map((item: any) => ({
            label: String(item.center),
            value: String(item.center),
          }))
        : [];

      setCenterOptions(mappedOptions);
    } catch {
      setCenterOptions([]);
      setCenterSearchError("Não foi possível buscar os Centros.");
    }
  };

  const handleCenterInputChange = (value: string, reason: string) => {
    if (reason === "reset") return;

    setCenterInput(value);
    clearFieldError("center");
    setCenterSearchError("");
    clearSavedState();
  };

  const handleCenterSelection = (options: OptionsCenter[]) => {
    setPendingCenters(options);
    clearFieldError("center");
    clearSavedState();
  };

  const handleCenterAdd = () => {
    if (pendingCenters.length === 0) {
      setFieldErrors((current) => ({
        ...current,
        center: "Selecione ao menos um Centro antes de adicionar.",
      }));
      return;
    }

    const pendingValues = pendingCenters
      .map((center) => center.value.trim())
      .filter(Boolean);

    if (pendingValues.length === 0) {
      setFieldErrors((current) => ({
        ...current,
        center: "Informe ao menos um Centro antes de adicionar.",
      }));
      return;
    }

    const newValues = pendingValues.filter(
      (value, index) =>
        pendingValues.findIndex(
          (currentValue) => normalizeValue(currentValue) === normalizeValue(value),
        ) === index &&
        !values.center.some(
          (currentValue) => normalizeValue(currentValue) === normalizeValue(value),
        ),
    );

    if (newValues.length === 0) {
      setFieldErrors((current) => ({
        ...current,
        center: "Os Centros selecionados já foram adicionados.",
      }));
      return;
    }

    const centerField = ruleFields.find((field) => field.id === "center");
    setValues((current) => ({
      ...current,
      center: centerField?.multiValue === false ? [newValues[0]] : [...current.center, ...newValues],
    }));
    setPendingCenters([]);
    setCenterInput("");
    setCenterOptions([]);
    clearFieldError("center");
    clearSavedState();
  };

  const handleSelectAllCenters = (checked: boolean) => {
    setValues((current) => {
      if (checked) {
        setPreviousCenterValues(current.center);
        return { ...current, center: [] };
      }

      return { ...current, center: previousCenterValues };
    });
    setAllCenters(checked);
    setPendingCenters([]);
    setCenterInput("");
    setCenterOptions([]);
    clearFieldError("center");
    clearSavedState();
  };

  const handleTextInputChange = (
    fieldId: TextRuleFieldId,
    value: string,
  ) => {
    setTextInputs((current) => ({ ...current, [fieldId]: value }));
    clearFieldError(fieldId);
    clearSavedState();
  };

  const handleTextValueAdd = (fieldId: TextRuleFieldId) => {
    const value = textInputs[fieldId].trim();

    if (!value) {
      setFieldErrors((current) => ({
        ...current,
        [fieldId]: "Informe um valor antes de adicionar.",
      }));
      return;
    }

    const alreadyAdded = values[fieldId].some(
      (currentValue) => normalizeValue(currentValue) === normalizeValue(value),
    );

    if (alreadyAdded) {
      setFieldErrors((current) => ({
        ...current,
        [fieldId]: "Este valor já foi adicionado.",
      }));
      return;
    }

    const fieldConfig = ruleFields.find((field) => field.id === fieldId);
    setValues((current) => ({
      ...current,
      [fieldId]: fieldConfig?.multiValue === false
        ? [value]
        : [...current[fieldId], value],
    }));
    setTextInputs((current) => ({ ...current, [fieldId]: "" }));
    clearFieldError(fieldId);
    clearSavedState();
  };

  const handleRemoveValue = (fieldId: RuleFieldId, value: string) => {
    setValues((current) => ({
      ...current,
      [fieldId]: current[fieldId].filter((currentValue) => currentValue !== value),
    }));
    clearFieldError(fieldId);
    clearSavedState();
  };

  const handleOptionalToggle = (fieldId: TextRuleFieldId, checked: boolean) => {
    setEnabledFields((current) => ({ ...current, [fieldId]: checked }));

    if (!checked) {
      setValues((current) => ({ ...current, [fieldId]: [] }));
      setTextInputs((current) => ({ ...current, [fieldId]: "" }));
      clearFieldError(fieldId);
    }

    clearSavedState();
  };

  const validateRules = (): FieldErrors => {
    const nextErrors: FieldErrors = {};

    ruleFields.forEach((field) => {
      if (!field.required && !enabledFields[field.id]) return;

      const hasValue =
        field.id === "center"
          ? allCenters || values.center.length > 0
          : values[field.id].length > 0;

      if (!hasValue) {
        nextErrors[field.id] = "Adicione pelo menos um valor para este campo.";
      }
    });

    return nextErrors;
  };

  const buildRulesPayload = (): UpdateGroupRuleRequest[] => {
    const currentRules = groupRules?.rules ?? ruleFields.map((field) => ({
      field: field.id,
      label: field.label,
      required: field.required,
      active: enabledFields[field.id],
      multiValue: field.multiValue,
      values: values[field.id],
    }));

    return currentRules.map((rule) => {
      if (!isRuleFieldId(rule.field)) {
        return {
          field: rule.field,
          active: rule.active,
          values: [...rule.values],
        };
      }

      const active = rule.required || enabledFields[rule.field];
      const ruleValues = rule.field === "center" && allCenters
        ? [ALL_CENTERS_VALUE]
        : values[rule.field];

      return {
        field: rule.field,
        active,
        values: active ? [...ruleValues] : [],
      };
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmittingRef.current || isBusy) {
      return;
    }

    const nextErrors = validateRules();
    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (!groupId) {
      setNotificationIcon(IconAlert);
      showNotification("Não foi possível identificar o grupo para salvar as regras.", "#FFD9D9");
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await dispatch(
        updateGroupRules({
          groupId,
          rules: buildRulesPayload(),
        }),
      ).unwrap();

      setFieldErrors({});
      await dispatch(getGroupRules(groupId)).unwrap();
      setNotificationIcon(SucessIcon);
      showNotification("Regras salvas com sucesso.");
    } catch (error: any) {
      setNotificationIcon(IconAlert);
      showNotification(
        Textnotifications.getSaveGroupRulesError(error?.message),
        "#FFD9D9",
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const renderFieldError = (fieldId: RuleFieldId) => {
    const error = fieldErrors[fieldId];
    if (!error) return null;

    return (
      <div role="alert" className="rulesFieldError">
        <ContainerError
          id={`rule-error-${fieldId}`}
          label={error}
          icon={IconAlert}
          widthError="100%"
          backgroudError="#FFD9D9"
          colorError="#000000"
        />
      </div>
    );
  };

  const renderSelectedValues = (fieldId: RuleFieldId) => {
    if (fieldId === "center" && allCenters) {
      return (
        <span className="rulesValueChip rulesValueChipAll">
          Todos
        </span>
      );
    }

    return values[fieldId].map((value) => (
      <span className="rulesValueChip" key={`${fieldId}-${value}`}>
        <span>{value}</span>
        <button
          type="button"
          aria-label={`Remover ${value} de ${fieldId}`}
          onClick={() => handleRemoveValue(fieldId, value)}
        >
          ×
        </button>
      </span>
    ));
  };

  if (rulesStatus === "loading" && !groupRules) {
    return (
      <div className="rulesGroupForm" data-testid="rules-group-loading">
        <SkeletonWrapper loading={true} />
      </div>
    );
  }

  if (rulesStatus === "error" && !groupRules) {
    return (
      <div className="rulesGroupForm" data-testid="rules-group-error">
        <ContainerError
          id="rule-error-load"
          label="Não foi possível carregar as regras do grupo."
          icon={IconAlert}
          widthError="100%"
          backgroudError="#FFD9D9"
          colorError="#000000"
        />
        <div className="rulesFormActions">
          <button
            type="button"
            className="rulesSaveButton"
            onClick={() => groupId && dispatch(getGroupRules(groupId))}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {isBusy &&
        createPortal(
          <div
            className="rulesSavingOverlay"
            data-testid="rules-group-saving"
            role="status"
            aria-live="polite"
            aria-label="Salvando regras"
            aria-busy="true"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 100000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              cursor: "wait",
            }}
          >
            <CircularProgress size={56} thickness={4} />
          </div>,
          document.body,
        )}
      <form
        className="rulesGroupForm"
        data-testid="rules-group-form"
        onSubmit={handleSubmit}
        noValidate
        aria-busy={isBusy}
        {...(isBusy ? ({ inert: "" } as Record<string, string>) : {})}
      >
      <div className="rulesInfoMessage" role="note">
        <img src={InfoIcon} alt="" />
        <p>
          Selecione quais campos farão parte da regra do grupo e atribua um ou
          mais valores para cada um.
        </p>
      </div>

      <div className="rulesGroupFields">
        {ruleFields.map((field) => {
          const isEnabled = enabledFields[field.id];

          return (
            <section
              className="rulesFieldSection"
              data-testid={`rule-field-${field.id}`}
              key={field.id}
              aria-labelledby={`rule-title-${field.id}`}
            >
              <div className="rulesFieldHeader">
                <div className="rulesFieldHeading">
                  <CustomCheckbox
                    checked={isEnabled}
                    disabled={field.required}
                    onChange={(_, checked) => {
                      if (!field.required && field.mode === "text") {
                        handleOptionalToggle(field.id, checked);
                      }
                    }}
                    inputProps={{ "aria-label": field.label }}
                    icon={<span className="rulesCheckboxIcon" />}
                    checkedIcon={<CustomCheckIcon />}
                  />
                  <div className="rulesFieldText">
                    <h3 id={`rule-title-${field.id}`} className="rulesFieldTitle">
                      {field.label}
                      {field.required && (
                        <span className="rulesRequiredMark" aria-label="obrigatório">
                          *
                        </span>
                      )}
                    </h3>
                    <p className="rulesFieldDescription">{field.description}</p>
                    {field.id === "center" && (
                      <label className="rulesSelectAll">
                        <CustomCheckbox
                          checked={allCenters}
                          onChange={(_, checked) => handleSelectAllCenters(checked)}
                          inputProps={{ "aria-label": "Selecionar Todos" }}
                          icon={<span className="rulesCheckboxIcon" />}
                          checkedIcon={<CustomCheckIcon />}
                        />
                        <span>Selecionar Todos</span>
                      </label>
                    )}
                  </div>
                </div>
                {field.required && (
                  <div className="rulesFieldHeaderActions">
                    <span className="rulesRequiredBadge">OBRIGATÓRIO</span>
                  </div>
                )}
              </div>

              {(field.required || isEnabled) && (
                <div className="rulesFieldBody">
                  <div
                    className="rulesSelectedValues"
                    aria-label={`Valores selecionados de ${field.label}`}
                  >
                    {renderSelectedValues(field.id)}
                  </div>

                  {field.mode === "center" ? (
                    <div className="rulesFieldAddRow">
                      <div className="rulesCenterAutocomplete">
                        <InputCheckBox
                          value={pendingCenters}
                          onChange={(_, newValue) =>
                            handleCenterSelection(newValue as OptionsCenter[])
                          }
                          placeholder="Digite um valor de Centro"
                          inputAriaLabel="Centro"
                          width="100%"
                          options={centerOptions}
                          storageKey={`rulesGroupCenter-${groupId ?? ""}`}
                          setInputValue={(value) =>
                            handleCenterInputChange(value, "input")
                          }
                          inputValue={centerInput}
                          setSelectedOptionsT={setPendingCenters}
                          clearState={() => setCenterOptions([])}
                          isCenter={true}
                          onSearchCenter={handleCenterSearch}
                          minSearchLength={0}
                          isReadOnly={allCenters}
                          disabled={allCenters}
                        />
                        {centerSearchError && (
                          <p className="rulesCenterSearchError" role="alert">
                            {centerSearchError}
                          </p>
                        )}
                        {renderFieldError(field.id)}
                      </div>
                      <ButtonGeneric
                        type="button"
                        title="Adicionar"
                        ariaLabel="Adicionar valor em Centro"
                        img={PlusButtonFilter}
                        style="buttonHeading rulesAddButton"
                        colorTitle="#ffffff"
                        onClick={handleCenterAdd}
                        disabled={allCenters || pendingCenters.length === 0}
                      />
                    </div>
                  ) : (
                    <div className="rulesFieldAddRow">
                      <div className="rulesTextInput">
                        <Input
                          label=""
                          width="100%"
                          placeholder={`Digite um valor de ${field.label}`}
                          value={textInputs[field.id]}
                          onChange={(event) =>
                            handleTextInputChange(field.id, event.target.value)
                          }
                          inputProps={{ "aria-label": field.label }}
                        />
                        {renderFieldError(field.id)}
                      </div>
                      <ButtonGeneric
                        type="button"
                        title="Adicionar"
                        ariaLabel={`Adicionar valor em ${field.label}`}
                        img={PlusButtonFilter}
                        style="buttonHeading rulesAddButton"
                        colorTitle="#ffffff"
                        onClick={() => handleTextValueAdd(field.id)}
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <div className="rulesFormActions">
        <ButtonGeneric
          type="button"
          title="Cancelar"
          ariaLabel="Cancelar"
          style="buttonBack rulesCancelButton"
          colorTitle="#007bc0"
          onClick={() =>
            navigate(
              backToGroupsList ? "/grupos-responsabilidade" : "/home-tax",
            )
          }
        />
        <ButtonGeneric
          type="submit"
          title={isBusy ? "Salvando..." : "Salvar Regras"}
          ariaLabel="Salvar Regras"
          style="buttonHeading rulesSaveButton"
          colorTitle="#ffffff"
          disabled={isBusy}
        />
      </div>
      </form>

      {notification && (
        <Notification
          label={labelNotification}
          backgroundError={backgroundColor}
          setNotification={setNotification}
          img={notificationIcon}
        />
      )}
    </>
  );
};

export default RegrasGrupoForm;